import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './AppleStyle.css'
import { auth, googleProvider } from './firebase';
import { signInWithPopup, signOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';

const Header = () => {

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [authForm, setAuthForm] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [resetEmail, setResetEmail] = useState('');
    const [authError, setAuthError] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    const navbarRef = useRef(null); // Ref to track the navbar element
    const mobileMenuRef = useRef(null); //Ref for the mobile menu
    const [user, setUser] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            setUser(currentUser);
        });

        return () => unsubscribe();
    }, []);

    //Dynamically adjust logo size based on screen width
    useEffect(() => {
        const handleScroll = () => {
            if(window.scrollY > 20) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        }

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    //UseEffect to handle click outside of the menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            //Only close if clicking outside both the navbar and mobile menu
            if(
                navbarRef.current && 
                !navbarRef.current.contains(event.target) &&
                mobileMenuRef.current &&
                !mobileMenuRef.current.contains(event.target)
            ) {
                closeMenu(); //Close the menu if the click is outside the navbar
            }
        };

        if(isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        //Clean up the event listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isMenuOpen]); //Re-run the effet when isMenuOpen changes

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        document.body.style.overflow = !isMenuOpen ? 'hidden' : '';
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        document.body.style.overflow = '';
    };

    const closeAuthModal = () => {
        setShowAuthModal(false);
        setIsSignUp(false);
        setAuthForm({ name: '', email: '', password: '', confirmPassword: '' });
        setAuthError('');
        setIsLoading(false);
    };

    const closePasswordResetModal = () => {
        setShowPasswordResetModal(false);
        setResetEmail('');
        setResetError('');
        setResetSuccess(false);
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setResetError('');

        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetSuccess(true);
        } catch (error) {
            let errorMessage = 'An error occurred';
            
            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email address';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                default:
                    errorMessage = error.message;
            }
            
            setResetError(errorMessage);
        }
        
        setIsLoading(false);
    };

    const handleFormChange = (e) => {
        setAuthForm({
            ...authForm,
            [e.target.name]: e.target.value
        });
        setAuthError('');
    };

    // Google Sign-In handler
    const handleGoogleSignIn = () => {
        signInWithPopup(auth, googleProvider)
            .then((result) => {
                setUser(result.user);
                closeAuthModal();
                //Close mobile menu if open
                if(isMenuOpen) {
                    closeMenu();
                }
            })
            .catch((err) => setAuthError(err.message));
    };

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setAuthError('');

        try {
            if (isSignUp) {
                // Sign up validation
                if (!authForm.name.trim()) {
                    setAuthError('Name is required');
                    setIsLoading(false);
                    return;
                }
                if (authForm.password !== authForm.confirmPassword) {
                    setAuthError('Passwords do not match');
                    setIsLoading(false);
                    return;
                }
                if (authForm.password.length < 6) {
                    setAuthError('Password must be at least 6 characters');
                    setIsLoading(false);
                    return;
                }

                const result = await createUserWithEmailAndPassword(auth, authForm.email, authForm.password);
                
                // Update the user's profile with their name
                await updateProfile(result.user, {
                    displayName: authForm.name.trim()
                });
                
                setUser(result.user);
            } else {
                // Sign in
                const result = await signInWithEmailAndPassword(auth, authForm.email, authForm.password);
                setUser(result.user);
            }
            
            closeAuthModal();
            if(isMenuOpen) {
                closeMenu();
            }

            // Scroll to top after successful authentication
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } catch (error) {
            let errorMessage = 'An error occurred';
            
            switch (error.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'This email is already registered';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Invalid email address';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak';
                    break;
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email';
                    break;
                case 'auth/wrong-password':
                    errorMessage = 'Incorrect password';
                    break;
                case 'auth/invalid-credential':
                    errorMessage = 'Invalid email or password';
                    break;
                default:
                    errorMessage = error.message;
            }
            
            setAuthError(errorMessage);
        }
        
        setIsLoading(false);
    };

    const handleSignOut = () => {
        signOut(auth)
            .then(() => {
                setUser(null);
                if(isMenuOpen) {
                    closeMenu();
                }
            })
            .catch((err) => console.error('Sign out error:', err));
    };

    const showSignIn = () => {
        setIsSignUp(false);
        setShowAuthModal(true);
    };

    const showSignUp = () => {
        setIsSignUp(true);
        setShowAuthModal(true);
    };

    return (
        <>
            <nav 
                className={`apple-nav ${scrolled ? 'scrolled' : ''}`}
                ref={navbarRef}
                aria-label="Main navigation"
            >
                <div className='container'>
                    <div className='d-flex justify-content-between align-items-center' style={{height: 'var(--apple-nav-height)'}}>
                        {/* Logo */}
                        <Link className='navbar-brand' to='/'>
                            <img 
                                src="/assets/logo.png" 
                                alt="PilotHelper Logo" 
                                className="apple-logo" 
                            />
                        </Link>

                        {/*Desktop Navigation*/}
                        <div className='d-none d-lg-flex align-items-center'>
                            <Link to="/" className="apple-nav-link">Home</Link>
                            <Link to="/courses" className="apple-nav-link">Courses</Link>
                            <Link to="/team" className="apple-nav-link">Team</Link>                            
                            <Link to="/about" className="apple-nav-link">About</Link>
                            <Link to="/contact" className="apple-nav-link">Contact</Link>

                            {/* Desktop Sign In/Out buttons */}
                            {!user ? (
                                <div className="d-flex gap-2 ms-3">
                                    <button 
                                        className="apple-button-outline" 
                                        onClick={showSignIn}
                                    >
                                        Sign In
                                    </button>
                                    <button 
                                        className="apple-button" 
                                        onClick={showSignUp}
                                    >
                                        Sign Up
                                    </button>
                                </div>
                            ) : (
                                <div className="d-flex align-items-center gap-3 ms-3">
                                    <span className="text-muted small">
                                        Welcome, {user.displayName || user.email?.split('@')[0] || 'User'}
                                    </span>
                                    <button
                                        className="apple-button-outline"
                                        onClick={handleSignOut}
                                    >
                                        Sign Out
                                    </button>
                                </div>
                            )}
                        </div>

                         {/* Hamburger Menu Toggler */}
                        <button
                            className={`apple-menu-button d-lg-none ${isMenuOpen ? 'apple-menu-open' : ''}`}
                            onClick={toggleMenu}
                            aria-expanded={isMenuOpen}
                            aria-label='Toggle navigation'
                        >
                            <span className='apple-menu-icon'></span>
                        </button>
                    </div>
                </div>
            </nav>

            {/*Mobile Menu */}
            <div 
                className={`apple-mobile-menu ${isMenuOpen ? 'open' : ''}`}
                ref={mobileMenuRef}
            >
                <Link to="/" className='apple-mobile-menu-link' onClick={closeMenu}>Home</Link>
                <Link to="/courses" className="apple-mobile-menu-link" onClick={closeMenu}>Courses</Link>
                <Link to="/team" className="apple-mobile-menu-link" onClick={closeMenu}>Team</Link>
                <Link to="/about" className="apple-mobile-menu-link" onClick={closeMenu}>About</Link>
                <Link to="/contact" className="apple-mobile-menu-link" onClick={closeMenu}>Contact</Link>

                <div className='mt-4 mb-2'>
                    {!user ? (
                        <div className="d-flex flex-column gap-2">
                            <button
                                className="apple-button-outline apple-mobile-button"
                                onClick={showSignIn}
                            >
                                Sign In
                            </button>
                            <button
                                className="apple-button apple-mobile-button"
                                onClick={showSignUp}
                            >
                                Sign Up
                            </button>
                        </div>
                    ) : (
                        <div className="text-center">
                            <p className="text-muted mb-3">
                                Welcome, {user.displayName || user.email?.split('@')[0] || 'User'}
                            </p>
                            <button
                                className="apple-button-outline apple-mobile-button"
                                onClick={handleSignOut}
                            >
                                Sign Out
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Authentication Modal */}
            {showAuthModal && (
                <div
                    className="modal show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={closeAuthModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="auth-modal-title"
                >
                    <div
                        className="modal-dialog modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 id="auth-modal-title" className="modal-title">
                                    {isSignUp ? 'Create Account' : 'Sign In'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeAuthModal}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                {/* Google Sign In Button */}
                                <button
                                    onClick={handleGoogleSignIn}
                                    className="btn btn-outline-dark w-100 mb-3 d-flex align-items-center justify-content-center"
                                    disabled={isLoading}
                                >
                                    <img
                                        src="https://developers.google.com/identity/images/g-logo.png"
                                        alt="Google"
                                        width="20"
                                        className="me-2"
                                    />
                                    Continue with Google
                                </button>

                                <div className="text-center mb-3">
                                    <span className="text-muted">or</span>
                                </div>

                                {/* Error Message */}
                                {authError && (
                                    <div className="alert alert-danger" role="alert">
                                        {authError}
                                    </div>
                                )}

                                {/* Email/Password Form */}
                                <form onSubmit={handleEmailAuth}>
                                    {isSignUp && (
                                        <div className="mb-3">
                                            <label htmlFor="name" className="form-label">Full Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="name"
                                                name="name"
                                                value={authForm.name}
                                                onChange={handleFormChange}
                                                required
                                                disabled={isLoading}
                                                placeholder="Enter your full name"
                                            />
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label htmlFor="email" className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            name="email"
                                            value={authForm.email}
                                            onChange={handleFormChange}
                                            required
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="password" className="form-label">Password</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="password"
                                            name="password"
                                            value={authForm.password}
                                            onChange={handleFormChange}
                                            required
                                            disabled={isLoading}
                                            minLength="6"
                                        />
                                    </div>
                                    {isSignUp && (
                                        <div className="mb-3">
                                            <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                                            <input
                                                type="password"
                                                className="form-control"
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                value={authForm.confirmPassword}
                                                onChange={handleFormChange}
                                                required
                                                disabled={isLoading}
                                                minLength="6"
                                            />
                                        </div>
                                    )}
                                    <button 
                                        type="submit" 
                                        className="apple-button w-100 mb-3"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {isSignUp ? 'Creating Account...' : 'Signing In...'}
                                            </>
                                        ) : (
                                            isSignUp ? 'Create Account' : 'Sign In'
                                        )}
                                    </button>
                                </form>

                                {/* Forgot Password Link - Only show during sign in */}
                                {!isSignUp && (
                                    <div className="text-center mb-3">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-decoration-none"
                                            onClick={() => {
                                                closeAuthModal();
                                                setShowPasswordResetModal(true);
                                            }}
                                            style={{fontSize: '0.9rem'}}
                                        >
                                            Forgot your password?
                                        </button>
                                    </div>
                                )}

                                {/* Toggle between sign in and sign up */}
                                <div className="text-center">
                                    <span className="text-muted">
                                        {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        onClick={() => {
                                            setIsSignUp(!isSignUp);
                                            setAuthForm({ name: '', email: '', password: '', confirmPassword: '' });
                                            setAuthError('');
                                        }}
                                        disabled={isLoading}
                                    >
                                        {isSignUp ? 'Sign In' : 'Sign Up'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Password Reset Modal */}
            {showPasswordResetModal && (
                <div
                    className="modal show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={closePasswordResetModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="password-reset-modal-title"
                >
                    <div
                        className="modal-dialog modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 id="password-reset-modal-title" className="modal-title">
                                    Reset Password
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closePasswordResetModal}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                {!resetSuccess ? (
                                    <>
                                        <p className="text-muted mb-4">
                                            Enter your email address and we'll send you a link to reset your password.
                                        </p>

                                        {/* Error Message */}
                                        {resetError && (
                                            <div className="alert alert-danger" role="alert">
                                                {resetError}
                                            </div>
                                        )}

                                        <form onSubmit={handlePasswordReset}>
                                            <div className="mb-3">
                                                <label htmlFor="resetEmail" className="form-label">Email Address</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="resetEmail"
                                                    value={resetEmail}
                                                    onChange={(e) => setResetEmail(e.target.value)}
                                                    required
                                                    disabled={isLoading}
                                                    placeholder="Enter your email address"
                                                />
                                            </div>
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary w-100 mb-3"
                                                disabled={isLoading}
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Sending Reset Email...
                                                    </>
                                                ) : (
                                                    'Send Reset Email'
                                                )}
                                            </button>
                                        </form>

                                        <div className="text-center">
                                            <span className="text-muted">
                                                Remember your password? 
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 ms-1"
                                                onClick={() => {
                                                    closePasswordResetModal();
                                                    setShowAuthModal(true);
                                                }}
                                                disabled={isLoading}
                                            >
                                                Sign In
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center" role="status" aria-live="polite">
                                        <div className="text-success mb-3">
                                            <i className="fas fa-check-circle fa-3x" aria-hidden="true"></i>
                                        </div>
                                        <h5 className="mb-3">Reset Email Sent!</h5>
                                        <p className="text-muted mb-4">
                                            We've sent a password reset link to <strong>{resetEmail}</strong>. 
                                            Check your email and follow the instructions to reset your password.
                                        </p>
                                        <p className="text-muted small mb-4">
                                            Don't see the email? <strong>Check your spam folder or try again with a different email address.</strong>
                                        </p>
                                        <button 
                                            className="btn btn-primary"
                                            onClick={closePasswordResetModal}
                                        >
                                            Done
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;